<script lang="ts">
  import { lang as langState } from '../lang-state.svelte.js';

  let lang = $derived(langState.current);

  type ExpertiseJson = Record<string, Record<string, Record<string, number | string>>>;

  // TU22+ raised cap to 31 (Elevated tier). expertise.json carries per-slot CoreRollBonus +
  // AttributeRollBonus per bucket (gear_chest/gear_back/etc.) — averaged for the summary row.
  const MAX_GRADE = 31;
  const ELEVATED = 31;
  const GEAR_BUCKETS = ['gear_chest', 'gear_back', 'gear_gloves', 'gear_mask', 'gear_holster', 'gear_kneepads'];

  let expertiseJson = $state<ExpertiseJson | null>(null);

  interface Row {
    grade: number;
    weaponWd: number;
    /** Average CoreRollBonus across 6 gear slots, as % (e.g. 24 = +24%). Slot-specific values shown in per-slot table. */
    armorPct: number;
    skillPct: number;
    isElevated: boolean;
  }

  function levelKey(g: number): string {
    return g === ELEVATED ? 'level_31_elevated' : `level_${g}`;
  }

  function avgCoreBonus(j: ExpertiseJson | null, g: number): number {
    if (!j) return g * 0.008 * 100; // fallback: matches raw 0.008 per grade per slot * 100 to %
    let sum = 0; let n = 0;
    for (const b of GEAR_BUCKETS) {
      const v = j[b]?.[levelKey(g)]?.CoreRollBonus;
      if (typeof v === 'number') { sum += v; n++; }
    }
    return n > 0 ? (sum / n) * 100 : 0;
  }

  function skillBonus(j: ExpertiseJson | null, g: number): number {
    // skill bucket has no level_31_elevated — clamp to 30.
    const key = g >= 30 ? 'level_30' : `level_${g}`;
    const v = j?.skill?.[key]?.SkillPowerBonus;
    return typeof v === 'number' ? v * 100 : g * 1.0;
  }

  function weaponBonus(j: ExpertiseJson | null, g: number): number {
    // Use weapon_ar as the canonical weapon WD curve (all 8 weapon buckets identical: +1%/grade).
    const v = j?.weapon_ar?.[levelKey(g)]?.WeaponDamage;
    return typeof v === 'number' ? v * 100 : g * 1.0;
  }

  let rows = $derived.by<Row[]>(() => {
    const out: Row[] = [];
    for (let g = 0; g <= MAX_GRADE; g++) {
      out.push({
        grade: g,
        weaponWd: weaponBonus(expertiseJson, g),
        armorPct: avgCoreBonus(expertiseJson, g),
        skillPct: skillBonus(expertiseJson, g),
        isElevated: g === ELEVATED,
      });
    }
    return out;
  });

  // Per-slot bonus table for G30 + G31 — shows that per-slot CoreRollBonus differs from old hardcoded g*0.3.
  let perSlotRows = $derived.by(() => {
    const j = expertiseJson;
    const rows30: Record<string, { core: number; attr: number }> = {};
    const rows31: Record<string, { core: number; attr: number }> = {};
    for (const b of GEAR_BUCKETS) {
      const l30 = j?.[b]?.level_30;
      const l31 = j?.[b]?.level_31_elevated;
      rows30[b] = {
        core: typeof l30?.CoreRollBonus === 'number' ? l30.CoreRollBonus * 100 : 0,
        attr: typeof l30?.AttributeRollBonus === 'number' ? l30.AttributeRollBonus * 100 : 0,
      };
      rows31[b] = {
        core: typeof l31?.CoreRollBonus === 'number' ? l31.CoreRollBonus * 100 : 0,
        attr: typeof l31?.AttributeRollBonus === 'number' ? l31.AttributeRollBonus * 100 : 0,
      };
    }
    return { rows30, rows31 };
  });

  const MILESTONES = [1, 5, 10, 15, 20, 25, 30, 31];

  const SLOT_LABEL_RU: Record<string, string> = {
    gear_chest: 'Бронежилет',
    gear_back: 'Рюкзак',
    gear_gloves: 'Перчатки',
    gear_mask: 'Маска',
    gear_holster: 'Кобура',
    gear_kneepads: 'Наколенники',
  };
  const SLOT_LABEL_EN: Record<string, string> = {
    gear_chest: 'Chest',
    gear_back: 'Backpack',
    gear_gloves: 'Gloves',
    gear_mask: 'Mask',
    gear_holster: 'Holster',
    gear_kneepads: 'Kneepads',
  };

  interface EscTier { hp: number; ar: number; dmg: number; label: string }
  interface EscMutator { harvester: number; suppressor: number; anchor: number; aid: number }
  interface EscReward { deposit: number; profit: number; group: number }

  let escTiers = $state<Record<string, EscTier>>({});
  let escMutators = $state<Record<string, EscMutator>>({});
  let escRewards = $state<Record<string, EscReward>>({});
  let escDrop = $state<Record<string, number>>({});

  $effect(() => {
    void lang;
    fetch('/data/expertise.json').then((r) => r.ok ? r.json() : null).then((j) => { if (j) expertiseJson = j; });
    fetch('/data/escalation-tiers.json').then((r) => r.ok ? r.json() : null).then((j) => { if (j) escTiers = j; });
    fetch('/data/escalation-mutators.json').then((r) => r.ok ? r.json() : null).then((j) => { if (j) escMutators = j; });
    fetch('/data/escalation-rewards.json').then((r) => r.ok ? r.json() : null).then((j) => { if (j) escRewards = j; });
    fetch('/data/escalation-drop.json').then((r) => r.ok ? r.json() : null).then((j) => { if (j) escDrop = j; });
  });

  let escTierIds = $derived(Object.keys(escTiers).map((k) => parseInt(k, 10)).sort((a, b) => a - b));
  let escMutIds = $derived(Object.keys(escMutators).map((k) => parseInt(k, 10)).sort((a, b) => a - b));
</script>

<section class="panel exp-header">
  <div class="panel-title">
    <span>{lang === 'ru' ? 'Экспертиза' : 'Expertise'}</span>
  </div>
  <div class="intro">
    {lang === 'ru' ? 'Экспертиза добавляет мультипликативный бонус к урону оружия, броне и навыкам. Максимальный уровень — 31 (Elevated, TU22+). G31 разблокирует повторные броски талантов и модов.' : 'Expertise Grade adds a multiplicative bonus to weapon damage, armor, and skills. Max Grade is 31 (Elevated, TU22+). G31 unlocks talent and mod rerolls.'}
  </div>
</section>

<div class="exp-grid">
  {#each MILESTONES as g (g)}
    {@const r = rows[g]}
    <div class="milestone" class:top={g === 30} class:elevated={g === ELEVATED} class:mid={g >= 10 && g < 30}>
      <div class="m-grade num">
        G{g}
        {#if g === ELEVATED}<span class="elev-badge">ELEVATED</span>{/if}
      </div>
      <div class="m-stats">
        <div class="ms"><span class="k">WD</span><span class="v num">+{r?.weaponWd.toFixed(0)}%</span></div>
        <div class="ms"><span class="k">{lang === 'ru' ? 'Ядро ср.' : 'Core avg'}</span><span class="v num">+{r?.armorPct.toFixed(1)}%</span></div>
        <div class="ms"><span class="k">{lang === 'ru' ? 'Навыки' : 'Skill'}</span><span class="v num">+{r?.skillPct.toFixed(1)}%</span></div>
      </div>
    </div>
  {/each}
</div>

<section class="panel table-wrap">
  <div class="panel-title"><span>{lang === 'ru' ? 'Бонусы по слотам (G30 и G31 Elevated)' : 'Per-Slot Bonuses (G30 and G31 Elevated)'}</span></div>
  <div class="intro" style="padding: 0 14px 8px">
    {lang === 'ru' ? 'CoreRollBonus — % к Core-роллу. AttributeRollBonus — % к мелкому атрибуту. Берётся напрямую из expertise.json по слоту.' : 'CoreRollBonus = % added to core stat roll. AttributeRollBonus = % added to minor attribute roll. Read per-slot from expertise.json.'}
  </div>
  <table>
    <thead>
      <tr>
        <th>{lang === 'ru' ? 'Слот' : 'Slot'}</th>
        <th class="num">G30 Core</th>
        <th class="num">G30 Attr</th>
        <th class="num">G31 Core</th>
        <th class="num">G31 Attr</th>
      </tr>
    </thead>
    <tbody>
      {#each GEAR_BUCKETS as b (b)}
        {@const r30 = perSlotRows.rows30[b]}
        {@const r31 = perSlotRows.rows31[b]}
        <tr>
          <td>{lang === 'ru' ? SLOT_LABEL_RU[b] : SLOT_LABEL_EN[b]}</td>
          <td class="num">+{r30?.core.toFixed(1)}%</td>
          <td class="num">+{r30?.attr.toFixed(1)}%</td>
          <td class="num elev">+{r31?.core.toFixed(1)}%</td>
          <td class="num elev">+{r31?.attr.toFixed(1)}%</td>
        </tr>
      {/each}
    </tbody>
  </table>
</section>

<section class="panel table-wrap">
  <div class="panel-title"><span>{lang === 'ru' ? 'Эскалация — масштабирование тиров' : 'Escalation — Tier Scaling'}</span></div>
  <div class="intro" style="padding: 0 14px 8px">
    {lang === 'ru' ? 'Режим Y8S1+ Countdown. Выше тир — больше HP/брони у врагов, больше наград и шанс дропа.' : 'Y8S1+ Countdown game mode. Higher tiers = more enemy HP/armor + higher rewards and drop chance.'}
  </div>
  <table class="esc-table">
    <thead>
      <tr>
        <th>{lang === 'ru' ? 'Тир' : 'Tier'}</th>
        <th class="num">{lang === 'ru' ? 'HP врагов' : 'Enemy HP'}</th>
        <th class="num">{lang === 'ru' ? 'Броня' : 'Enemy Armor'}</th>
        <th class="num">{lang === 'ru' ? 'Урон врагов' : 'Enemy DMG'}</th>
        <th class="num">{lang === 'ru' ? 'Шанс дропа' : 'Drop %'}</th>
      </tr>
    </thead>
    <tbody>
      {#each escTierIds as id (id)}
        {@const t = escTiers[String(id)]}
        <tr class:top={id === 10}>
          <td>{id === 0 ? (lang === 'ru' ? 'База' : 'Base') : 'T' + id}</td>
          <td class="num">+{t.hp}%</td>
          <td class="num">+{t.ar}%</td>
          <td class="num">{t.dmg}%</td>
          <td class="num">{id === 0 ? '—' : (escDrop[String(id)] ?? 0).toFixed(2) + '%'}</td>
        </tr>
      {/each}
    </tbody>
  </table>
</section>

{#if escMutIds.length > 0}
<section class="panel table-wrap">
  <div class="panel-title"><span>{lang === 'ru' ? 'Мутаторы по тирам' : 'Mutators by Tier'}</span></div>
  <table class="esc-table">
    <thead>
      <tr>
        <th>{lang === 'ru' ? 'Тир' : 'Tier'}</th>
        <th class="num">Harvester</th>
        <th class="num">Suppressor</th>
        <th class="num">Anchor</th>
        <th class="num">Aid</th>
      </tr>
    </thead>
    <tbody>
      {#each escMutIds as id (id)}
        {@const m = escMutators[String(id)]}
        <tr>
          <td>T{id}</td>
          <td class="num">{m.harvester === 0 ? '—' : m.harvester + '%'}</td>
          <td class="num">{m.suppressor === 0 ? '—' : m.suppressor + '%'}</td>
          <td class="num">{m.anchor === 0 ? '—' : m.anchor + '%'}</td>
          <td class="num">{m.aid === 0 ? '—' : m.aid + '%'}</td>
        </tr>
      {/each}
    </tbody>
  </table>
</section>
{/if}

<section class="panel table-wrap">
  <div class="panel-title"><span>{lang === 'ru' ? 'Все уровни' : 'All Grades'}</span></div>
  <table>
    <thead>
      <tr>
        <th>{lang === 'ru' ? 'Ур.' : 'Grade'}</th>
        <th class="num">{lang === 'ru' ? 'Урон оружия' : 'Weapon Damage'}</th>
        <th class="num">{lang === 'ru' ? 'Ядро ср.' : 'Core avg'}</th>
        <th class="num">{lang === 'ru' ? 'Навыки' : 'Skill'}</th>
      </tr>
    </thead>
    <tbody>
      {#each rows as r (r.grade)}
        <tr class:top={r.grade === 30} class:elevated={r.isElevated}>
          <td class="num">
            G{r.grade}{#if r.isElevated} <span class="elev-badge sm">ELEVATED</span>{/if}
          </td>
          <td class="num">+{r.weaponWd.toFixed(0)}%</td>
          <td class="num">+{r.armorPct.toFixed(1)}%</td>
          <td class="num">+{r.skillPct.toFixed(1)}%</td>
        </tr>
      {/each}
    </tbody>
  </table>
</section>

<style>
  .exp-header { max-width: 1100px; margin: 0 auto 14px; }
  .intro { color: var(--text-dim); font-size: 13px; line-height: 1.5; }
  .exp-grid {
    max-width: 1100px; margin: 0 auto 14px;
    display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 8px;
  }
  .milestone {
    padding: 14px;
    background: var(--card); border: 1px solid var(--border);
    border-left: 3px solid var(--blue);
    border-radius: var(--r); text-align: center;
  }
  .milestone.mid { border-left-color: var(--orange); }
  .milestone.top {
    border-left-color: var(--exotic);
    box-shadow: 0 0 20px rgba(255,107,61,.18);
  }
  .milestone.elevated {
    border-left-color: #ffd24a;
    box-shadow: 0 0 24px rgba(255,210,74,.28);
    background: linear-gradient(180deg, rgba(255,210,74,.08), var(--card));
  }
  .m-grade {
    font: 700 22px/1 var(--f-display); color: var(--orange);
    letter-spacing: .12em; margin-bottom: 8px;
  }
  .milestone.top .m-grade { color: var(--exotic); }
  .milestone.elevated .m-grade { color: #ffd24a; }
  .elev-badge {
    display: inline-block;
    margin-left: 6px;
    padding: 2px 6px;
    background: #ffd24a;
    color: #1a1a1a;
    font: 700 9px/1 var(--f-display);
    letter-spacing: .14em;
    border-radius: 3px;
    vertical-align: middle;
  }
  .elev-badge.sm { font-size: 8px; padding: 1px 4px; margin-left: 4px; }
  tr.elevated {
    background: rgba(255,210,74,.12);
    color: #ffd24a;
    font-weight: 700;
  }
  td.elev { color: #ffd24a; font-weight: 700; }
  .m-stats { display: flex; flex-direction: column; gap: 4px; font-size: 11px; }
  .ms { display: flex; justify-content: space-between; padding: 2px 6px; background: var(--bg-2); border-radius: 3px; }
  .ms .k { color: var(--muted); font-family: var(--f-display); font-weight: 700; letter-spacing: .1em; text-transform: uppercase; font-size: 9px; }
  .ms .v { color: var(--green); font-weight: 700; }

  .table-wrap { max-width: 800px; margin: 0 auto; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  table th, table td { padding: 6px 10px; border-bottom: 1px solid var(--border); }
  table th { font: 700 10px/1 var(--f-display); letter-spacing: .12em; color: var(--orange); text-transform: uppercase; text-align: left; }
  table th.num, table td.num { text-align: right; font-family: var(--f-mono); }
  tr.top { background: rgba(255,107,61,.1); color: var(--exotic-hi); font-weight: 700; }
  .esc-table { width: 100%; border-collapse: collapse; font-size: 12px; }
  .esc-table th, .esc-table td { padding: 6px 10px; border-bottom: 1px solid var(--border); }
  .esc-table th { font: 700 10px/1 var(--f-display); letter-spacing: .12em; color: var(--orange); text-transform: uppercase; text-align: left; }
  .esc-table th.num, .esc-table td.num { text-align: right; font-family: var(--f-mono); }
</style>
